<template>
  <div class="shoot-box" ref="shootBox" :style="{ '--scale': scale }">
    <div
      class="shoot-wrapper"
      :class="{ bordered: !isAutoExportPng }"
      :style="{
        width: `${boxWidth}px`,
        height: `${boxHeight}px`,
      }"
    >
      <div class="shoot-box-bg" @click="handleCanvasClick">
        <canvas
          ref="arrowCanvas"
          width="100"
          height="100"
          style="width: 100%; height: 100%"
        ></canvas>
      </div>
      <div class="shoot-box-legend">
        <div
          class="legend-column"
          v-for="(legend, index) in legendList"
          :key="index"
        >
          <div
            class="legend-item"
            v-for="item in legend"
            :key="item.type"
            :class="{ disabled: disabledKeys.includes(item.type) }"
            @click.stop="handleLegendClick(item)"
          >
            <div class="legend-color" :style="{ background: item.color }"></div>
            <span class="legend-label">{{ item.type }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import canvasToImage_base64_data_mixin from '@mixins/canvasToImage_base64_data'

const colorMap = [
  { type: 'Post', color: '#00ffce' }, // 射门中柱
  { type: 'Goal', color: '#ff0000' }, // 进球
  { type: 'Miss', color: '#00ff24' }, // 射偏
  { type: 'Saved', color: '#ffff00' }, // 扑救
  { type: 'Blocked', color: '#ff00e7' }, // 射门被封堵
]

export default {
  name: 'shoot_map',
  mixins: [canvasToImage_base64_data_mixin],
  props: {
    isAutoExportPng: {
      type: Boolean,
      default: false,
    },
    data: {
      type: Array,
      default: () => [],
    },
    dataType: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      boxWidth: 0,
      boxHeight: 0,
      scale: 1,
      disabledKeys: [],
    }
  },
  computed: {
    legend() {
      if (this.dataType === 'shot') {
        return colorMap
      } else {
        return [...colorMap, { type: 'ChanceMissed', color: '#6900ff' }] // 绝佳得分机会没能打进
      }
    },
    legendList() {
      const list = this.legend.reduce((acc, curr, index) => {
        if (index % 2 === 0) {
          acc.push([curr])
        } else {
          acc[acc.length - 1].push(curr)
        }
        return acc
      }, [])
      return list
    },
    lines() {
      const result = this.data.filter(
        (item) => !this.disabledKeys.includes(item.shot_result)
      )
      return result
    },
  },
  watch: {
    lines: {
      handler(newVal) {
        if (newVal) {
          this.$nextTick(() => {
            this.draw(newVal)
            if (this.isAutoExportPng) {
              const tempCanvas = this.createCanvas()
              const imgdata = this.upLoad(tempCanvas)
              this.$emit('uploadData', imgdata)
            }
          })
        }
      },
      deep: true,
      immediate: true,
    },
  },
  mounted() {
    this.calculateSize()
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => this.calculateSize())
      this.resizeObserver.observe(this.$refs.shootBox)
    }
  },
  beforeDestroy() {
    if (this.resizeObserver) this.resizeObserver.disconnect()
  },
  methods: {
    calculateSize() {
      const parent = this.$refs.shootBox
      if (!parent) return

      const targetRatio = 2 / 1.5
      const parentWidth = parent.offsetWidth
      const parentHeight = parent.offsetHeight
      const parentRatio = parentWidth / parentHeight
      this.scale = Number((parentWidth / window.innerWidth).toFixed(4))

      if (parentRatio > targetRatio) {
        this.boxHeight = parentHeight
        this.boxWidth = Math.floor(parentHeight * targetRatio)
      } else {
        this.boxWidth = parentWidth
        this.boxHeight = Math.floor(parentWidth / targetRatio)
      }
    },
    draw(values) {
      const canvas = this.$refs.arrowCanvas
      if (!canvas || !this.data) return
      const dpr = window.devicePixelRatio || 1
      const rect = canvas.getBoundingClientRect()
      canvas.width = rect.width * dpr
      canvas.height = rect.height * dpr
      const ctx = canvas.getContext('2d')
      ctx.scale(dpr, dpr)
      ctx.clearRect(0, 0, rect.width, rect.height)

      values
        .sort((a, b) => (a.shot_result === 'Goal') - (b.shot_result === 'Goal'))
        .forEach((value) => {
          const { start_x, start_y, end_x, end_y } = value
          const x1 = Math.floor(((100 - start_y) / 100) * rect.width)
          const y1 = Math.floor(((100 - start_x) / 100) * rect.height)
          const x2 = Math.floor(((100 - end_y) / 100) * rect.width)
          const y2 = Math.floor(((100 - end_x) / 100) * rect.height)
          const color = this.legend.find(
            (item) => item.type === value.shot_result
          )?.color
          this.drawArrow({ x1, y1, x2, y2 }, color, value.shot_result)
        })
    },
    drawArrow(value, color, type) {
      const canvas = this.$refs.arrowCanvas
      const ctx = canvas.getContext('2d')
      const { x1, y1, x2, y2 } = value

      // 设置全局线条样式
      ctx.strokeStyle = color
      ctx.lineWidth = 3
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'

      const arrowLength = 10 // 箭头长度
      const arrowAngle = Math.PI / 6 // 箭头夹角（30度）
      // 计算线段朝向的角度（弧度）
      const angle = Math.atan2(y2 - y1, x2 - x1)

      const lineEndX = x2 - (arrowLength - 3) * Math.cos(angle)
      const lineEndY = y2 - (arrowLength - 3) * Math.sin(angle)

      if (type === 'Goal') {
        // 绘制圆点
        ctx.beginPath()
        ctx.arc(x1, y1, 4, 0, Math.PI * 2)
        ctx.fillStyle = color
        ctx.fill()
      }

      // 2. 绘制主干线段
      ctx.beginPath()
      ctx.moveTo(x1, y1)
      ctx.lineTo(lineEndX, lineEndY)
      ctx.stroke()

      // 计算箭头左侧顶点坐标
      const leftX = x2 - arrowLength * Math.cos(angle - arrowAngle)
      const leftY = y2 - arrowLength * Math.sin(angle - arrowAngle)

      // 计算箭头右侧顶点坐标
      const rightX = x2 - arrowLength * Math.cos(angle + arrowAngle)
      const rightY = y2 - arrowLength * Math.sin(angle + arrowAngle)

      // 绘制并填充箭头三角形
      ctx.beginPath()
      ctx.moveTo(x2, y2) // 尖端（终点）
      ctx.lineTo(leftX, leftY) // 左翼
      ctx.lineTo(rightX, rightY) // 右翼
      ctx.closePath() // 闭合路径形成三角形
      ctx.fillStyle = color // 箭头填充色
      ctx.fill()
    },

    handleLegendClick(item) {
      if (this.disabledKeys.includes(item.type)) {
        this.disabledKeys = this.disabledKeys.filter((key) => key !== item.type)
      } else {
        this.disabledKeys.push(item.type)
      }
    },

    createCanvas() {
      const canvas = this.$refs.arrowCanvas
      if (!canvas) return
      const fullWidth = canvas.width
      const fullHeight = canvas.height
      const cropHeight = fullHeight / 2
      const tempCanvas = document.createElement('canvas')
      tempCanvas.width = fullWidth
      tempCanvas.height = cropHeight

      const tempCtx = tempCanvas.getContext('2d')
      tempCtx.drawImage(
        canvas,
        0,
        0,
        fullWidth,
        cropHeight, // 从原画布左上角开始，裁剪出上半部分
        0,
        0,
        fullWidth,
        cropHeight // 完整填充到临时画布
      )
      return tempCanvas
    },

    handleCanvasClick(e) {
      e.stopPropagation()
      if (!this.isAutoExportPng) return
      const tempCanvas = this.createCanvas()
      this.downLoad(tempCanvas)
    },

    upLoadImg() {
      const tempCanvas = this.createCanvas()
      const imgdata = this.upLoad(tempCanvas)
      this.$emit('uploadData', imgdata)
    },
  },
}
</script>

<style lang="scss" scoped>
.shoot-box {
  flex: 1;
  .shoot-wrapper {
    position: relative;
    margin: 0 auto;
    background-image: var(--mixtmt_ball_ver_bg);
    background-size: 100% 200%;
    background-repeat: no-repeat;
    background-position: center top;
    width: 100%;
    height: 100%;
    overflow: hidden;
    &.bordered {
      border: 0.416vw solid var(--mixtmt_echarts_footBallPlaygroundColor);
      border-radius: 0.694vw;
      background-color: var(--mixtmt_echarts_footBallPlaygroundColor);
    }
    .shoot-box-bg {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 200%;
      background-image: url(~@/assets/images/ball_shoot_bg.png);
      background-size: 100%;
      background-repeat: no-repeat;
      background-position: center;
    }
    .shoot-box-legend {
      position: absolute;
      bottom: 3%;
      left: 2%;
      display: flex;
      justify-content: flex-start;
      gap: calc(2vw * var(--scale));
      .legend-column {
        display: flex;
        flex-direction: column;
        justify-content: flex-end;
        .legend-item {
          display: flex;
          align-items: center;
          font-size: calc(2vw * var(--scale));
          height: calc(2vw * var(--scale));
          gap: 0.2vw;
          cursor: pointer;
          &:last-child {
            margin-top: 0.12vw;
          }
          &.disabled {
            .legend-color {
              background-color: #999 !important;
            }
          }
          .legend-color {
            width: calc(3vw * var(--scale));
            height: calc(2px * var(--scale));
            border-radius: 2px;
            flex-shrink: 0;
          }
        }
      }
    }
  }
}
</style>
